import { observable } from '@zto/zpage'
import { request } from './request'

import type { AppContextOptions } from '@zto/zpage'
import type { UploaderOptions } from './types'

const { Observable } = observable

export class ZFSUploaderCabinet {
  config: any = {
    retryCount: 3
  }

  retryCount = 0
  aborted = false
  progress: any = null
  xhrList: any[] = []
  xhrHandler = (xhr: any) => this.xhrList.push(xhr)
  onData = (e?: any) => {}
  onError = (err?: any) => {}
  onComplete = (e?: any) => {}
  uploadUrl: string
  filefield = 'uploadfile'
  file: File
  data: any

  constructor(options: UploaderOptions, handlers: object) {
    const env = options.app.env

    this.config = Object.assign(this.config, options.config)
    this.uploadUrl = options.uploadUrl // 用服务端返回的地址
    this.filefield = options.filefield || this.filefield
    this.file = options.file
    this.data = options.data
    Object.assign(this, handlers)
  }

  static async upload(file: File, options: AppContextOptions) {
    const app = options.app

    options = {
      onProgress: (e: any) => {},
      onUpload: (e: any) => {},
      ...options
    }

    // 获取OSS文件上传签名信息
    const _fileSuffix = file.name.split('.').pop()?.toLowerCase() || 'jpg'
    const fileSuffix = '.' + _fileSuffix // 获取file的后缀
    let signatureRes = await app.getUploadToken(fileSuffix).catch((err: any) => {
      return Promise.reject({
        noBreaking: true, // 非中断错误，可以重新尝试
        message: err
      })
    })

    let uploadOptions = {
      app,
      file,
      data: signatureRes,
      uploadUrl: signatureRes.host
    }

    return new Promise((resolve, reject) => {
      const observable = new Observable((observer: observable.Observer) => {
        const uploader = new ZFSUploaderCabinet(uploadOptions, {
          onData: (e: any) => observer.next(e),
          onError: (e: any) => observer.error(e),
          onComplete: (e: any) => observer.complete(e)
        })
        uploader.putFile()
        return uploader.stop.bind(uploader)
      })

      const subscription = observable.subscribe({
        next(res: any) {
          options.onProgress(res)
        },
        error(err: any) {
          reject(err)
        },
        complete(result: any) {
          if (result && result.status) {
            resolve(result)
          } else {
            reject(result)
          }
        }
      })

      options.onUpload({ subscription, observable })
    })
  }

  async putFile() {
    this.aborted = false

    return this.directUpload().then(
      (res: any) => {
        this.onComplete(res.data)
      },
      (err: any) => {
        this.clear()

        let needRetry = err.isRequestError && err.code === 0 && !this.aborted
        let notReachRetryCount = ++this.retryCount <= this.config.retryCount
        if (needRetry && notReachRetryCount) {
          this.putFile()
          return
        }

        this.onError(err)
      }
    )
  }

  clear() {
    this.xhrList.forEach(xhr => xhr.abort())
    this.xhrList = []
  }

  stop() {
    this.clear()
    this.aborted = true
  }

  // 直传（OSS）
  directUpload() {
    let formData = new FormData()
    formData.append('success_action_status', this.data.successActionStatus)
    formData.append('policy', this.data.policy)
    formData.append('x-oss-signature', this.data.xossSignature)
    formData.append('x-oss-signature-version', this.data.xossSignatureVersion)
    formData.append('x-oss-credential', this.data.xossCredential)
    formData.append('x-oss-date', this.data.xossDate)
    formData.append('key', this.data.filePath) // 文件名
    formData.append('x-oss-security-token', this.data.securityToken)
    formData.append('file', this.file) // file 必须为最后一个表单域

    return request(this.uploadUrl, {
      method: 'POST',
      body: formData,
      onProgress: (data: any) => {
        this.updateDirectProgress(data.loaded, data.total)
      },
      onCreate: this.xhrHandler
    }).then(() => {
      const res = {
        // OSS不给返回值，故拼接此结构
        data: {
          message: '上传成功',
          status: true,
          statusCode: 'success',
          result: {
            url: this.data.fileFullUrl,
            fileName: this.data.fileFullUrl
          }
        }
      }
      this.finishDirectProgress()
      return res
    })
  }

  updateDirectProgress(loaded: number, total: number) {
    // 当请求未完成时可能进度会达到100，所以total + 1来防止这种情况出现
    this.progress = { total: this.getProgressInfoItem(loaded, total + 1) }
    this.onData(this.progress)
  }

  finishDirectProgress() {
    // 在某些浏览器环境下，xhr 的 progress 事件无法被触发，progress 为 null， 这里 fake 下
    if (!this.progress) {
      this.progress = { total: this.getProgressInfoItem(this.file.size, this.file.size) }
      this.onData(this.progress)
      return
    }

    let total = this.progress.total
    this.progress = { total: this.getProgressInfoItem(total.loaded + 1, total.size) }
    this.onData(this.progress)
  }

  getProgressInfoItem(loaded: number, size: number) {
    return {
      loaded: loaded,
      size: size,
      percent: (loaded / size) * 100
    }
  }
}
